import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";

const EFFECTIVE_DATE = "May 25, 2026";
const CONTACT_EMAIL = "privacy@ndakubet.com";
const SUPPORT_EMAIL = "support@ndakubet.com";
const SITE = "https://ndakubet.com";

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24 space-y-3" data-testid={`privacy-section-${id}`}>
    <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-foreground">
      {title}
    </h2>
    <div className="text-sm sm:text-base text-foreground/80 leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

const TOC_ITEMS = [
  ["overview",            "1. Overview"],
  ["who-we-are",          "2. Who We Are"],
  ["information",         "3. Information We Collect"],
  ["how-we-use",          "4. How We Use Your Information"],
  ["legal-bases",         "5. Legal Bases (GDPR)"],
  ["sharing",             "6. How We Share Information"],
  ["third-parties",       "7. Third-Party Services"],
  ["cookies",             "8. Cookies & Similar Technologies"],
  ["retention",           "9. Data Retention"],
  ["security",            "10. Security"],
  ["your-rights",         "11. Your Rights"],
  ["children",            "12. Children's Privacy"],
  ["international",       "13. International Data Transfers"],
  ["responsible-gambling","14. Responsible Gambling"],
  ["mobile-app",          "15. Mobile App-Specific Disclosures"],
  ["changes",             "16. Changes to This Policy"],
  ["contact",             "17. Contact Us"],
];

export default function PrivacyPage() {
  return (
    <main
      className="min-h-screen bg-background text-foreground"
      data-testid="privacy-page"
    >
      {/* Hero */}
      <div className="border-b border-border/40 bg-gradient-to-b from-amber-500/5 to-transparent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors mb-6"
            data-testid="privacy-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to home
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-amber-400" />
            </div>
            <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-amber-400/90">
              Legal · Privacy
            </span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
            Privacy Policy
          </h1>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
            How NDAKUBET collects, uses, shares, and protects your information when you use our
            website, mobile applications, and related services.
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground/80">Effective date:</span> {EFFECTIVE_DATE} ·
            <span className="font-semibold text-foreground/80"> Last updated:</span> {EFFECTIVE_DATE}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        {/* TOC */}
        <aside className="lg:sticky lg:top-24 lg:self-start order-2 lg:order-1">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">
            Contents
          </p>
          <nav className="flex flex-col gap-1.5 text-xs sm:text-[13px]" data-testid="privacy-toc">
            {TOC_ITEMS.map(([id, label]) => (
              <a
                key={id}
                href={`#${id}`}
                className="text-muted-foreground hover:text-foreground transition-colors py-0.5"
                data-testid={`privacy-toc-${id}`}
              >
                {label}
              </a>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <article className="order-1 lg:order-2 space-y-10">
          <Section id="overview" title="1. Overview">
            <p>
              NDAKUBET (&quot;NDAKUBET&quot;, &quot;we&quot;, &quot;our&quot;, or &quot;us&quot;)
              operates a football prediction platform that aggregates fixture, statistical, and
              market data from multiple licensed sources and produces probabilistic forecasts.
              This Privacy Policy describes the categories of personal information we collect, how
              we use it, with whom we share it, and the choices and rights available to you.
            </p>
            <p>
              By accessing or using {SITE}, our Android/iOS applications, or any related service
              (collectively, the &quot;Service&quot;), you acknowledge that you have read and
              understood this Policy.
            </p>
          </Section>

          <Section id="who-we-are" title="2. Who We Are">
            <p>
              NDAKUBET is the data controller for the personal information processed under this
              Policy. For privacy-specific inquiries, you can reach us at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-amber-400 hover:underline">
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </Section>

          <Section id="information" title="3. Information We Collect">
            <p>We collect the following categories of information:</p>

            <h3 className="font-semibold text-foreground/90 pt-1">a. Information you provide</h3>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                <span className="font-semibold">Account data:</span> email address, display name,
                hashed password, and (optional) WhatsApp number when you opt in to broadcasts.
              </li>
              <li>
                <span className="font-semibold">Preferences:</span> favourite leagues and teams,
                notification settings, and language/region preferences.
              </li>
              <li>
                <span className="font-semibold">Saved betslips and bookmarks:</span> custom betslip
                selections and other content you choose to save inside the Service.
              </li>
              <li>
                <span className="font-semibold">Support communications:</span> messages you send to
                our support channels.
              </li>
            </ul>

            <h3 className="font-semibold text-foreground/90 pt-1">b. Information collected automatically</h3>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                <span className="font-semibold">Device & technical data:</span> IP address, device
                model, operating system, browser type, screen resolution, locale, and crash logs.
              </li>
              <li>
                <span className="font-semibold">Usage data:</span> pages visited, features used,
                clicks on fixtures or predictions, session duration, and referral URL.
              </li>
              <li>
                <span className="font-semibold">Performance & analytics:</span> aggregated
                performance metrics such as page load time and API latency.
              </li>
            </ul>

            <h3 className="font-semibold text-foreground/90 pt-1">c. Information from third parties</h3>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                Football data feeds (fixtures, statistics, odds) from our licensed data partners.
                These feeds do not contain personal data about you.
              </li>
              <li>
                Authentication data from OAuth providers if you sign in via a third-party login
                (only the email and name associated with that account).
              </li>
            </ul>

            <p className="text-xs text-muted-foreground italic pt-1">
              We do not knowingly collect special categories of personal data (e.g., race,
              religion, biometric data, health data, or precise geolocation).
            </p>
          </Section>

          <Section id="how-we-use" title="4. How We Use Your Information">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>To create and maintain your account and authenticate your sessions.</li>
              <li>
                To deliver the core Service: prediction lists, halftime betslips, accuracy
                analytics, and historical track records.
              </li>
              <li>
                To send service notifications (e.g., your daily double drop on WhatsApp) when you
                explicitly opt in.
              </li>
              <li>
                To improve our prediction models by aggregating outcome data and calibration
                signals — never using personally identifiable information.
              </li>
              <li>
                To detect, prevent, and respond to abuse, fraud, automated scraping, and security
                incidents.
              </li>
              <li>To comply with applicable laws, regulations, and lawful requests.</li>
            </ul>
          </Section>

          <Section id="legal-bases" title="5. Legal Bases for Processing (GDPR / UK GDPR)">
            <p>If you are in the European Economic Area, United Kingdom, or other jurisdictions that require a legal basis for processing, we rely on:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                <span className="font-semibold">Contract</span> — to provide the Service you
                requested by creating an account.
              </li>
              <li>
                <span className="font-semibold">Legitimate interests</span> — to operate, secure,
                and improve the Service, provided your rights do not override these interests.
              </li>
              <li>
                <span className="font-semibold">Consent</span> — for optional features such as
                WhatsApp broadcasts and marketing communications, which you may withdraw at any
                time.
              </li>
              <li>
                <span className="font-semibold">Legal obligation</span> — to comply with tax,
                accounting, or law-enforcement requirements.
              </li>
            </ul>
          </Section>

          <Section id="sharing" title="6. How We Share Information">
            <p>We do not sell your personal information. We share data only as described below:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                <span className="font-semibold">Service providers (sub-processors)</span> who host
                infrastructure, send transactional messages, or perform analytics on our behalf,
                under written contracts that restrict them to processing data only for our
                purposes.
              </li>
              <li>
                <span className="font-semibold">Legal & safety:</span> when required by law, court
                order, or to protect our rights, users, or the integrity of the Service.
              </li>
              <li>
                <span className="font-semibold">Business transfers:</span> if NDAKUBET is involved
                in a merger, acquisition, or asset sale, your information may transfer as part of
                that transaction, subject to the same protections.
              </li>
              <li>
                <span className="font-semibold">With your consent:</span> any other sharing is done
                only with your explicit authorisation.
              </li>
            </ul>
          </Section>

          <Section id="third-parties" title="7. Third-Party Services">
            <p>The following sub-processors help us run the Service. We have entered into data-processing agreements with each.</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                <span className="font-semibold">MongoDB Atlas</span> — managed database hosting.
              </li>
              <li>
                <span className="font-semibold">Cloud infrastructure providers</span> — server,
                CDN, and storage hosting.
              </li>
              <li>
                <span className="font-semibold">API-Sports / RapidAPI</span> — football fixture
                and statistics feeds (no personal data is sent).
              </li>
              <li>
                <span className="font-semibold">Twilio</span> — WhatsApp delivery (only for users
                who opt in and only the phone number you provide).
              </li>
              <li>
                <span className="font-semibold">Emergent LLM / OpenAI / Google AI</span> —
                language and image generation used for player cutouts and editorial summaries
                (input is limited to public match metadata).
              </li>
            </ul>
            <p className="text-xs text-muted-foreground">
              The current list of sub-processors may evolve. We will update this section when we
              add or remove a provider.
            </p>
          </Section>

          <Section id="cookies" title="8. Cookies & Similar Technologies">
            <p>
              We use first-party cookies and browser <code className="text-xs px-1 py-0.5 rounded bg-muted/60">localStorage</code> to:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Keep you signed in across sessions.</li>
              <li>Remember UI preferences such as theme and selected leagues.</li>
              <li>
                Persist user-generated content such as manually regenerated daily-double
                betslips (stored locally on your device, not on our servers).
              </li>
              <li>Measure aggregated, non-identifying usage to debug performance issues.</li>
            </ul>
            <p>
              You can clear cookies and local storage at any time via your browser settings, but
              doing so will sign you out and reset your preferences.
            </p>
          </Section>

          <Section id="retention" title="9. Data Retention">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                <span className="font-semibold">Account data:</span> retained while your account
                is active and for up to 24 months after deletion to satisfy legal, accounting, or
                fraud-prevention obligations.
              </li>
              <li>
                <span className="font-semibold">Operational logs:</span> retained for up to 90
                days, then automatically purged or anonymised.
              </li>
              <li>
                <span className="font-semibold">Aggregated analytics & model-calibration data:</span>{" "}
                may be retained indefinitely in fully anonymised form.
              </li>
              <li>
                <span className="font-semibold">Support communications:</span> retained for up to
                36 months for service quality and dispute resolution.
              </li>
            </ul>
          </Section>

          <Section id="security" title="10. Security">
            <p>We apply industry-standard technical and organisational safeguards including:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>TLS 1.2+ encryption for all data in transit.</li>
              <li>Passwords stored using a salted bcrypt hash; never in plaintext.</li>
              <li>Role-based access controls and audit logging for internal staff.</li>
              <li>Regular dependency scanning and security patching.</li>
            </ul>
            <p>
              No system is perfectly secure. If we become aware of a personal data breach that is
              likely to result in a risk to your rights, we will notify you and the relevant
              supervisory authority within 72 hours, as required by law.
            </p>
          </Section>

          <Section id="your-rights" title="11. Your Rights">
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Access the personal data we hold about you.</li>
              <li>Correct inaccurate or incomplete data.</li>
              <li>Request deletion of your data (&quot;right to be forgotten&quot;).</li>
              <li>Restrict or object to certain processing.</li>
              <li>Receive your data in a portable, machine-readable format.</li>
              <li>Withdraw consent at any time for processing that relies on consent.</li>
              <li>
                Lodge a complaint with your local data protection authority (e.g., the
                Information Commissioner&apos;s Office in the UK, your national DPA in the EEA, or
                the Office of the Privacy Commissioner in your jurisdiction).
              </li>
            </ul>
            <p>
              To exercise any of these rights, email{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-amber-400 hover:underline">
                {CONTACT_EMAIL}
              </a>
              . We respond within 30 days; complex requests may take longer and we will inform you
              if so.
            </p>
          </Section>

          <Section id="children" title="12. Children's Privacy">
            <p>
              The Service is intended for users who are at least 18 years old (or the legal age of
              majority in their jurisdiction). We do not knowingly collect personal information
              from children. If you believe a child has provided us with personal data, contact us
              at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-amber-400 hover:underline">
                {CONTACT_EMAIL}
              </a>{" "}
              and we will delete it promptly.
            </p>
          </Section>

          <Section id="international" title="13. International Data Transfers">
            <p>
              We operate globally, and your information may be processed in countries outside your
              own. When we transfer personal data from the European Economic Area, United Kingdom,
              or Switzerland to a country that has not received an adequacy decision, we use
              appropriate safeguards such as Standard Contractual Clauses or equivalent measures.
            </p>
          </Section>

          <Section id="responsible-gambling" title="14. Responsible Gambling Notice">
            <p>
              NDAKUBET provides statistical predictions and educational content. We are not a
              bookmaker and do not accept bets, wagers, or stakes. Football outcomes are uncertain
              and past performance does not guarantee future results.
            </p>
            <p>
              If you choose to use third-party betting operators, please bet responsibly and only
              with money you can afford to lose. Help is available at{" "}
              <a href="https://www.gamcare.org.uk" target="_blank" rel="noreferrer" className="text-amber-400 hover:underline">
                GamCare
              </a>
              ,{" "}
              <a href="https://www.begambleaware.org" target="_blank" rel="noreferrer" className="text-amber-400 hover:underline">
                BeGambleAware
              </a>
              , or your local responsible-gambling helpline.
            </p>
          </Section>

          <Section id="mobile-app" title="15. Mobile Application Disclosures">
            <p>
              Our Android and iOS applications request only the permissions strictly necessary to
              operate the Service:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                <span className="font-semibold">Internet access</span> to fetch fixtures,
                predictions, and live updates.
              </li>
              <li>
                <span className="font-semibold">Notifications</span> (optional) to deliver score
                alerts and your daily double drop.
              </li>
              <li>
                <span className="font-semibold">Storage</span> (optional) to cache content for
                offline viewing.
              </li>
            </ul>
            <p>
              We do not collect contacts, SMS, call logs, microphone, camera, or precise location
              data through the mobile app. Crash diagnostics are limited to anonymised stack
              traces and device model information.
            </p>
            <p className="text-xs text-muted-foreground">
              If you uninstall the app, locally cached data is removed by your operating system.
              Account data on our servers persists until you request deletion.
            </p>
          </Section>

          <Section id="changes" title="16. Changes to This Policy">
            <p>
              We may update this Policy to reflect changes in our practices, legal obligations, or
              the Service itself. When we make material changes, we will revise the &quot;Last
              updated&quot; date at the top of this page and, where appropriate, notify you by
              email or in-app message before the changes take effect. Continued use of the Service
              after the effective date constitutes acceptance of the updated Policy.
            </p>
          </Section>

          <Section id="contact" title="17. Contact Us">
            <p>If you have questions, concerns, or requests regarding this Privacy Policy, please contact us:</p>
            <div className="rounded-xl border border-border bg-card/50 p-4 sm:p-5 text-sm space-y-1.5">
              <p>
                <span className="font-semibold">Privacy team:</span>{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-amber-400 hover:underline" data-testid="privacy-contact-email">
                  {CONTACT_EMAIL}
                </a>
              </p>
              <p>
                <span className="font-semibold">General support:</span>{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-amber-400 hover:underline">
                  {SUPPORT_EMAIL}
                </a>
              </p>
              <p>
                <span className="font-semibold">Website:</span>{" "}
                <a href={SITE} className="text-amber-400 hover:underline">{SITE}</a>
              </p>
            </div>
          </Section>

          <div className="text-[11px] text-muted-foreground/70 pt-6 border-t border-border/40">
            © {new Date().getFullYear()} NDAKUBET. All rights reserved. This Privacy Policy is
            provided for informational purposes and does not constitute legal advice.
          </div>
        </article>
      </div>
    </main>
  );
}
